import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, PenLine } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, Pastille, PastilleSite } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { messageRefus } from '../../lib/useFormateurs';
import {
  useCreerFiche,
  useModifierFiche,
  useRenvoyerFiche,
  useRetirerFiche,
  useSoumettreFiche,
  useValiderFiche,
  type InterventionADocumenter,
  type LigneFiche,
  type SaisieFiche,
} from '../../lib/useEspaceFormateur';
import { delaiLisible, demiJourneeLisible, heuresLisibles, horodatage, jourLisible } from './formats-fiche';

// ---------------------------------------------------------------------------
// Briques
// ---------------------------------------------------------------------------

const CLASSE_CHAMP =
  'w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-codialis-navy focus:border-codialis-mint focus:bg-white focus:outline-none disabled:cursor-not-allowed disabled:opacity-70';

function Champ({ libelle, aide, children }: { libelle: string; aide?: string; children: ReactNode }): JSX.Element {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">{libelle}</span>
      {children}
      {aide && <span className="mt-1 block text-[11px] text-codialis-gray">{aide}</span>}
    </label>
  );
}

function Message({ ton, children }: { ton: 'danger' | 'warning' | 'info'; children: ReactNode }): JSX.Element {
  const classes = {
    danger: 'bg-red-50 text-red-800',
    warning: 'bg-amber-50 text-amber-900',
    info: 'bg-codialis-sand text-codialis-navy',
  }[ton];
  return (
    <p role={ton === 'danger' ? 'alert' : undefined} className={`rounded-xl px-3 py-2 text-sm ${classes}`}>
      {children}
    </p>
  );
}

function Information({ libelle, valeur }: { libelle: string; valeur: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">{libelle}</div>
      <div className="mt-0.5 break-words text-sm text-codialis-navy">{valeur}</div>
    </div>
  );
}

/** Émargement de la demi-journée, lu sur la séance : la fiche n'en tient pas de copie. */
function BlocEmargement({ ligne }: { ligne: LigneFiche | InterventionADocumenter }): JSX.Element {
  const emargement = ligne.emargement;
  if (!emargement) {
    return <Message ton="info">Aucune demi-journée n’est rattachée à cette fiche.</Message>;
  }
  return (
    <div className="rounded-xl border border-gray-100 bg-codialis-sand/60 p-3 sm:p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Émargement</div>
        <Link to="/emargement" className="text-xs font-semibold text-codialis-sky hover:underline">
          Ouvrir l’émargement
        </Link>
      </div>
      <div className="mt-2 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
        <div className="flex items-center gap-2">
          {emargement.clos ? (
            <CheckCircle2 className="h-4 w-4 shrink-0 text-codialis-teal" aria-hidden="true" />
          ) : (
            <Clock className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
          )}
          <span className="text-codialis-navy">{emargement.clos ? 'Feuille clôturée' : 'Feuille ouverte'}</span>
        </div>
        <div className="flex items-center gap-2">
          {emargement.attesteLe ? (
            <PenLine className="h-4 w-4 shrink-0 text-codialis-teal" aria-hidden="true" />
          ) : (
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
          )}
          <span className="text-codialis-navy">
            {emargement.attesteLe ? `Attestée le ${horodatage(emargement.attesteLe)}` : 'Séance non attestée'}
          </span>
        </div>
        <div className="text-codialis-navy">
          {emargement.attendus === 0
            ? 'Aucun apprenant attendu'
            : `${emargement.presents}/${emargement.attendus} présent${emargement.presents > 1 ? 's' : ''}`}
          {emargement.absents > 0 && ` · ${emargement.absents} absent${emargement.absents > 1 ? 's' : ''}`}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fenêtre de la fiche
// ---------------------------------------------------------------------------

export type CibleFiche =
  | { type: 'fiche'; ligne: LigneFiche }
  | { type: 'intervention'; ligne: InterventionADocumenter };

interface Brouillon {
  titre: string;
  objectifs: string;
  observations: string;
  heures: string;
}

function brouillonDe(ligne: LigneFiche | null): Brouillon {
  return {
    titre: ligne?.titre ?? '',
    objectifs: ligne?.objectifs ?? '',
    observations: ligne?.observations ?? '',
    heures: ligne?.heuresRealisees !== null && ligne?.heuresRealisees !== undefined
      ? String(ligne.heuresRealisees).replace('.', ',')
      : '',
  };
}

function saisieDe(brouillon: Brouillon): SaisieFiche | string {
  const texte = brouillon.heures.trim().replace(',', '.');
  const heures = texte === '' ? null : Number(texte);
  if (heures !== null && (!Number.isFinite(heures) || heures <= 0)) {
    return 'Les heures réalisées sont un nombre positif, par exemple 3,5.';
  }
  return {
    titre: brouillon.titre,
    objectifs: brouillon.objectifs,
    observations: brouillon.observations,
    heuresRealisees: heures,
  };
}

/**
 * Fiche séquence d'une demi-journée (CDC §7.3). Le formateur la rédige et la
 * soumet ; le pôle pédagogique la valide ou la renvoie. Ce qui empêche un geste
 * vient de l'API, ligne par ligne.
 */
export function FicheSequence({
  cible,
  peutValider,
  onFermer,
}: {
  cible: CibleFiche;
  peutValider: boolean;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const idBlocs = useId();
  const fiche = cible.type === 'fiche' ? cible.ligne : null;
  const ligne = cible.ligne;
  const [brouillon, setBrouillon] = useState<Brouillon>(() => brouillonDe(fiche));
  const [modifie, setModifie] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [motif, setMotif] = useState('');
  const [renvoiOuvert, setRenvoiOuvert] = useState(false);
  // Identifiant de la fiche, connu dès sa création : la liste relue peut
  // arriver après un second clic, qui ne doit pas créer une seconde fiche.
  const [ficheId, setFicheId] = useState<string | null>(fiche?.id ?? null);

  const creer = useCreerFiche();
  const modifier = useModifierFiche();
  const retirer = useRetirerFiche();
  const soumettre = useSoumettreFiche();
  const valider = useValiderFiche();
  const renvoyer = useRenvoyerFiche();
  const enCours =
    creer.isPending || modifier.isPending || retirer.isPending || soumettre.isPending || valider.isPending || renvoyer.isPending;

  // La fiche relue après un geste remplace la saisie, sauf ce qui est en cours.
  useEffect(() => {
    if (fiche) setFicheId(fiche.id);
    if (!modifie && fiche) setBrouillon(brouillonDe(fiche));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiche?.id, fiche?.titre, fiche?.objectifs, fiche?.observations, fiche?.heuresRealisees]);

  const editable =
    cible.type === 'intervention' ? cible.ligne.refusCreation === null : cible.ligne.refusModification === null;

  const changer = (champ: keyof Brouillon) => (valeur: string) => {
    setBrouillon((courant) => ({ ...courant, [champ]: valeur }));
    setModifie(true);
    setErreur(null);
  };

  /** Enregistre la saisie, en créant la fiche si besoin ; rend son identifiant. */
  async function enregistrer(): Promise<string | null> {
    const saisie = saisieDe(brouillon);
    if (typeof saisie === 'string') {
      setErreur(saisie);
      return null;
    }
    if (!ficheId) {
      if (!ligne.seanceId) return null;
      const { fiche: creee } = await creer.mutateAsync({ seanceId: ligne.seanceId, ...saisie });
      setFicheId(creee.id);
      setModifie(false);
      return creee.id;
    }
    if (modifie) await modifier.mutateAsync({ id: ficheId, saisie });
    setModifie(false);
    return ficheId;
  }

  async function enregistrerBrouillon(): Promise<void> {
    try {
      const id = await enregistrer();
      if (id) toast('Brouillon de la fiche enregistré');
    } catch (raison) {
      setErreur(messageRefus(raison, 'L’enregistrement a échoué.'));
    }
  }

  async function surSoumettre(): Promise<void> {
    try {
      const id = await enregistrer();
      if (!id) return;
      await soumettre.mutateAsync(id);
      toast('Fiche soumise au pôle pédagogique');
      onFermer();
    } catch (raison) {
      setErreur(messageRefus(raison, 'La soumission a échoué.'));
    }
  }

  async function surRetirer(): Promise<void> {
    if (!fiche) return;
    try {
      await retirer.mutateAsync(fiche.id);
      toast('Brouillon retiré');
      onFermer();
    } catch (raison) {
      setErreur(messageRefus(raison, 'Le retrait a échoué.'));
    }
  }

  async function surValider(): Promise<void> {
    if (!fiche) return;
    try {
      await valider.mutateAsync(fiche.id);
      toast('Fiche séquence validée');
      onFermer();
    } catch (raison) {
      setErreur(messageRefus(raison, 'La validation a échoué.'));
    }
  }

  async function surRenvoyer(evenement: FormEvent): Promise<void> {
    evenement.preventDefault();
    if (!fiche) return;
    if (motif.trim().length < 3) {
      setErreur('Précisez le motif du renvoi.');
      return;
    }
    try {
      await renvoyer.mutateAsync({ id: fiche.id, motif: motif.trim() });
      toast('Fiche renvoyée au formateur');
      onFermer();
    } catch (raison) {
      setErreur(messageRefus(raison, 'Le renvoi a échoué.'));
    }
  }

  const titre = `Fiche du ${jourLisible(ligne.date)}${ligne.demiJournee ? ` · ${demiJourneeLisible(ligne.demiJournee)}` : ''}`;
  const delai = delaiLisible(ligne);
  const refusSoumission = fiche?.refusSoumission ?? null;

  return (
    <Modale ouverte titre={titre} sousTitre={ligne.libelleSeance ?? undefined} onFermer={onFermer} largeur="max-w-3xl">
      <div className="space-y-4 p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <Pastille texte={ligne.etatLibelle} ton={ligne.etatTon} />
          {delai && <Pastille texte={delai} ton={ligne.horsDelai ? 'danger' : 'neutral'} />}
          {ligne.siteCode && <PastilleSite site={ligne.siteCode} />}
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Information libelle="Promotion" valeur={ligne.promotion ?? '—'} />
          <Information libelle="Créneau" valeur={ligne.creneau ?? '—'} />
          <Information libelle="Modalité" valeur={ligne.modalite === 'ELEARNING' ? 'E-learning' : ligne.modalite ? 'Présentiel' : '—'} />
          <Information libelle="Formateur" valeur={ligne.formateur} />
        </div>

        <BlocEmargement ligne={ligne} />

        {fiche?.motifRenvoi && fiche.etat === 'RENVOYEE' && (
          <Message ton="warning">
            Renvoyée le {horodatage(fiche.renvoyeeLe)}
            {fiche.renvoyeePar ? ` par ${fiche.renvoyeePar}` : ''} : {fiche.motifRenvoi}
          </Message>
        )}

        {editable ? (
          <form
            onSubmit={(evenement: FormEvent) => {
              evenement.preventDefault();
              void enregistrerBrouillon();
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <Champ libelle="Séquence" aide="Intitulé, par exemple le bloc du programme travaillé.">
                  <input
                    value={brouillon.titre}
                    onChange={(e) => changer('titre')(e.target.value)}
                    list={ligne.blocs.length > 0 ? idBlocs : undefined}
                    maxLength={200}
                    className={CLASSE_CHAMP}
                  />
                </Champ>
                {ligne.blocs.length > 0 && (
                  <datalist id={idBlocs}>
                    {ligne.blocs.map((bloc) => (
                      <option key={bloc} value={bloc} />
                    ))}
                  </datalist>
                )}
              </div>
              <Champ
                libelle="Heures réalisées"
                aide={ligne.heuresCreneau !== null ? `Créneau prévu : ${heuresLisibles(ligne.heuresCreneau)}` : undefined}
              >
                <input
                  value={brouillon.heures}
                  onChange={(e) => changer('heures')(e.target.value)}
                  inputMode="decimal"
                  placeholder={ligne.heuresCreneau !== null ? String(ligne.heuresCreneau).replace('.', ',') : ''}
                  className={CLASSE_CHAMP}
                />
              </Champ>
            </div>
            <Champ libelle="Objectifs travaillés">
              <textarea
                rows={3}
                value={brouillon.objectifs}
                onChange={(e) => changer('objectifs')(e.target.value)}
                maxLength={4000}
                className={CLASSE_CHAMP}
              />
            </Champ>
            <Champ libelle="Observations sur le groupe" aide="Facultatif.">
              <textarea
                rows={2}
                value={brouillon.observations}
                onChange={(e) => changer('observations')(e.target.value)}
                maxLength={4000}
                className={CLASSE_CHAMP}
              />
            </Champ>

            {erreur && <Message ton="danger">{erreur}</Message>}
            {!erreur && !modifie && refusSoumission && <Message ton="warning">{refusSoumission}</Message>}
            {!erreur && !fiche && ligne.emargement && !(ligne.emargement.clos && ligne.emargement.attesteLe) && (
              <Message ton="warning">{ligne.prochaineEtape}</Message>
            )}

            <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                {fiche && fiche.refusRetrait === null && !modifie && (
                  <button
                    type="button"
                    onClick={surRetirer}
                    disabled={enCours}
                    className="min-h-[44px] rounded-xl px-3 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-60"
                  >
                    Retirer le brouillon
                  </button>
                )}
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <BoutonSecondaire onClick={onFermer}>Fermer</BoutonSecondaire>
                <BoutonSecondaire onClick={() => void enregistrerBrouillon()} disabled={enCours}>
                  Enregistrer le brouillon
                </BoutonSecondaire>
                <BoutonPrincipal onClick={surSoumettre} disabled={enCours}>
                  Soumettre pour validation
                </BoutonPrincipal>
              </div>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            {fiche ? (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="sm:col-span-2">
                    <Information libelle="Séquence" valeur={fiche.titre || '—'} />
                  </div>
                  <Information libelle="Heures réalisées" valeur={heuresLisibles(fiche.heuresRealisees)} />
                </div>
                <Information
                  libelle="Objectifs travaillés"
                  valeur={<span className="whitespace-pre-line">{fiche.objectifs ?? '—'}</span>}
                />
                <Information
                  libelle="Observations sur le groupe"
                  valeur={<span className="whitespace-pre-line">{fiche.observations ?? '—'}</span>}
                />
                <div className="grid grid-cols-1 gap-3 text-xs text-codialis-gray sm:grid-cols-2">
                  {fiche.soumiseLe && <div>Soumise le {horodatage(fiche.soumiseLe)}</div>}
                  {fiche.valideeLe && (
                    <div>
                      Validée le {horodatage(fiche.valideeLe)}
                      {fiche.valideePar ? ` par ${fiche.valideePar}` : ''}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Message ton="info">{ligne.prochaineEtape} La fiche est rédigée par le formateur de la demi-journée.</Message>
            )}

            {erreur && <Message ton="danger">{erreur}</Message>}

            {peutValider && fiche && fiche.refusValidation === null && renvoiOuvert && (
              <form onSubmit={surRenvoyer} className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3 sm:p-4">
                <Champ libelle="Motif du renvoi" aide="Transmis au formateur, par mail et dans son espace.">
                  <textarea
                    rows={3}
                    value={motif}
                    onChange={(e) => {
                      setMotif(e.target.value);
                      setErreur(null);
                    }}
                    maxLength={1000}
                    className={CLASSE_CHAMP}
                  />
                </Champ>
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <BoutonSecondaire onClick={() => setRenvoiOuvert(false)}>Annuler</BoutonSecondaire>
                  <BoutonPrincipal type="submit" disabled={enCours}>
                    Renvoyer au formateur
                  </BoutonPrincipal>
                </div>
              </form>
            )}

            <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
              <BoutonSecondaire onClick={onFermer}>Fermer</BoutonSecondaire>
              {peutValider && fiche && fiche.refusRenvoi === null && !renvoiOuvert && (
                <BoutonSecondaire onClick={() => setRenvoiOuvert(true)} disabled={enCours}>
                  Renvoyer au formateur
                </BoutonSecondaire>
              )}
              {peutValider && fiche && fiche.refusValidation === null && (
                <BoutonPrincipal onClick={surValider} disabled={enCours}>
                  Valider la fiche
                </BoutonPrincipal>
              )}
            </div>
            {peutValider && fiche && fiche.refusValidation !== null && fiche.etat !== 'VALIDEE' && (
              <p className="text-right text-xs text-codialis-gray">{fiche.refusValidation}</p>
            )}
          </div>
        )}
      </div>
    </Modale>
  );
}
